<template>
  <div>
    <event-history-filters
      :types="types"
    />
    <confirm-modal
      v-if="showExcelDownloadModal"
      id="excel-download-modal"
      title="Information"
      :confirmation="confirmModalMessage"
      ok-button="Close"
      close-button=""
      @submit="showExcelDownloadModal = false"
    />
    <b-row>
      <b-col class="text-right px-0">
        <b-button
          variant="outline-primary"
          class="m-1"
          @click="downloadExcelUrl"
        >
          <FeatherIcon icon="DownloadIcon" />
          Download as Excel
        </b-button>
        <b-button
          :disabled="resendLoading"
          variant="outline-danger"
          class="m-1"
          @click="resendEvents"
        >
          <FeatherIcon icon="RefreshCwIcon" />
          Resend events
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-card>
          <b-table
            :fields="tableFields"
            :items="parcels"
            :busy="loading"
            show-empty
            responsive
            empty-text="No matching records found"
            class="mb-0"
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle mr-1" />
                <strong>Loading...</strong>
              </div>
            </template>

            <template
              v-if="userCompaniesCount > 1"
              #cell(logo)="data"
            >
              <CompanyThumb
                v-if="data.item.company"
                :logo="data.item.company.logo"
                :name="data.item.company.name"
              />
            </template>

            <template #cell(name)="data">
              <router-link :to="data.item.link">
                <strong :class="data.item.class">{{ data.item.name }}</strong>
              </router-link>
            </template>

            <template #cell(type)="data">
              <b-badge
                v-if="statusMappings[data.item.type]"
                pill
                variant="primary"
              >
                {{ statusMappings[data.item.type] }}
              </b-badge>
              <b-badge
                v-if="!statusMappings[data.item.type]"
                pill
                variant="light-primary"
              >
                {{ data.item.type }}
              </b-badge>
            </template>

            <template #cell(at)="data">
              <strong :class="data.item.class">{{ data.item.at | formatDate }}</strong>
            </template>

            <!-- Column: Actions -->
            <template #cell(actions)="data">
              <!-- View log button -->
              <b-button
                v-b-modal.callback-log-modal
                :variant="callbacksModalVariant(data.item.callback_logs)"
                size="sm"
                :disabled="!data.item.callback_logs.length"
                @click="selectedLogs = data.item.callback_logs"
              >
                Callbacks
              </b-button>
            </template>

          </b-table>
          <pagination :per-page="30" />
        </b-card>
      </b-col>
    </b-row>
    <callback-log-modal
      v-if="selectedLogs.length"
      :logs="selectedLogs"
    />
  </div>
</template>

<script>
import {
  BTable,
  BRow,
  BCol,
  BCard,
  BBadge,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import Vue from 'vue'
import EventHistoryFilters from '@/views/EventsHistory/EventHistoryFilters.vue'
import Pagination from '@/layouts/components/Pagination/Pagination.vue'
import handleError from '@/views/components/errorHandler'
import CallbackLogModal from '@/views/callbacks/modals/CallbackLogModal.vue'
import CompanyThumb from '@/views/components/CompanyThumb.vue'
import { searchQuery } from '@core/utils/filter'
import { EventBus } from '@core/utils/utils'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import excelDownloadMixin from '@/mixins/excelDownloadMixin'
import ConfirmModal from '@core/components/confirmation/confirmModal.vue'

export default {
  components: {
    ConfirmModal,
    BSpinner,
    CompanyThumb,
    CallbackLogModal,
    Pagination,
    EventHistoryFilters,
    BTable,
    BRow,
    BCol,
    BCard,
    BBadge,
    BButton,
  },
  mixins: [excelDownloadMixin],
  data() {
    return {
      loading: false,
      selectedLogs: [],
      types: [],
      page: 0,
      perPage: 30,
      sortBy: 'created_at',
      isSortDirDesc: true,
      parcels: [],
      filters: [],
      resendLoading: false,
      excelDownloadUrl: '',
    }
  },
  computed: {
    userCompaniesCount() {
      return Vue.prototype.$profile().data.user.companies.length
    },
    tableFields() {
      return [
        ...[this.userCompaniesCount > 1 ? { key: 'logo', label: 'Company', sortable: false, class: 'image-column' } : null],
        'document_number',
        'container_code',
        'tracking_number',
        'type',
        'at',
        'actions',
      ]
    },
    statusMappings() {
      const types = []
      this.types.forEach(item => {
        types[item.value] = item.label
      })
      return types
    },
  },
  created() {
    EventBus.$on('companyListUpdated', this.fetchData)
    EventBus.$on('updateFilters', this.handleUpdateFilters)
    EventBus.$on('pageChange', this.handlePageChange)
    this.fetchStatuses()
  },
  beforeDestroy() {
    EventBus.$off('updateFilters', this.handleUpdateFilters)
    EventBus.$off('pageChange', this.handlePageChange)
    EventBus.$off('companyListUpdated', this.fetchData)
  },
  methods: {
    downloadExcelUrl() {
      this.excelDownloadUrl = searchQuery(`${this.getUrl()}/excel`, this.filters)
      this.handleExcelDownload()
    },
    getUrl() {
      return `${process.env.VUE_APP_API_URL}/v1/api/events`
    },
    handleUpdateFilters(data) {
      this.filters = data
      this.page = data?.page
      this.fetchData()
    },
    handlePageChange(newPage) {
      this.page = newPage
      this.fetchData()
    },
    hasSuccessfulLogs(logs) {
      let found = false
      logs.forEach(log => {
        if (log.status === 'succeeded') {
          found = true
        }
      })

      return found
    },
    fetchStatuses() {
      this.$http.get('/v1/api/events/statuses')
        .then(response => {
          this.types = response.data.data
        })
    },
    fetchData() {
      this.loading = true
      this.filters = { ...this.filters, page: this.page, perPage: this.perPage }
      this.$http.get(searchQuery(this.getUrl(), this.filters))
        .then(response => {
          this.$data.parcels = response.data.data
          EventBus.$emit('updateMeta', response.data.meta)
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
        .finally(() => {
          this.loading = false
        })
    },
    callbacksModalVariant(logs) {
      let variant = ''

      if (logs.length) {
        if (!this.hasSuccessfulLogs(logs)) {
          variant = 'danger'
        }

        if (this.hasSuccessfulLogs(logs)) {
          variant = 'success'
        }
      }

      return variant
    },
    resendEvents() {
      this.resendLoading = true
      this.$http.post(`${this.getUrl()}/resend`, this.filters)
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Resend initiated',
              variant: 'success',
              solid: true,
            },
          })
          this.fetchData()
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
        .finally(() => {
          this.resendLoading = false
        })
    },
  },
}
</script>

<style scoped>

</style>
